/** @author Rajkumar Rathod */
import { log } from './utils';
import type { ResolvedConfig } from './config';
import type { PrefillData, WidgetFlow } from './types';

/**
 * Binds the trigger elements.
 *
 * Deliberately one delegated listener on `document` rather than `querySelectorAll` +
 * `addEventListener` per element. That means:
 *   - buttons rendered later (React/Vue/Angular mount, HTMX swap, modal, tab switch)
 *     work with no re-scan and no MutationObserver;
 *   - a framework re-render that replaces the DOM node does not orphan a listener;
 *   - the SDK holds no references to host DOM, so nothing leaks.
 */
export class TriggerBinder {
  private cfg: ResolvedConfig;
  private open: (flow: WidgetFlow, prefill: PrefillData, trigger: HTMLElement) => void;
  private bound = false;
  private warned = new Set<string>();

  constructor(
    cfg: ResolvedConfig,
    open: (flow: WidgetFlow, prefill: PrefillData, trigger: HTMLElement) => void
  ) {
    this.cfg = cfg;
    this.open = open;
  }

  bind(): void {
    if (this.bound) return;
    this.bound = true;
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('keydown', this.onKeyDown, true);
  }

  unbind(): void {
    if (!this.bound) return;
    this.bound = false;
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
  }

  private onClick = (event: MouseEvent): void => {
    // Let modified clicks (new tab / new window) and non-primary buttons fall through.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const hit = this.match(event.target);
    if (!hit) return;

    // Stops `<a href="/book">` from navigating away and `<button type=submit>` from
    // posting its form. The widget is the action now.
    event.preventDefault();
    event.stopPropagation();
    this.open(hit.flow, readPrefill(hit.el), hit.el);
  };

  /**
   * Enter/Space on a non-native trigger. A `<div data-ehr-widget="register">` gets no
   * synthetic click from the browser, so keyboard users would otherwise be locked out.
   */
  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented) return;
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    const hit = this.match(event.target);
    if (!hit) return;

    const tag = hit.el.tagName;
    // Native controls already synthesise a click from these keys — don't double-fire.
    if (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT') return;

    event.preventDefault();
    this.open(hit.flow, readPrefill(hit.el), hit.el);
  };

  private match(target: EventTarget | null): { flow: WidgetFlow; el: HTMLElement } | null {
    if (!(target instanceof Element)) return null;

    for (const [flow, selectors] of Object.entries(this.cfg.triggers)) {
      for (const selector of selectors) {
        let el: Element | null = null;
        try {
          el = target.closest(selector);
        } catch {
          // A malformed custom selector should not break every click on the page.
          if (!this.warned.has(selector)) {
            this.warned.add(selector);
            log.warn(`invalid trigger selector, ignoring: ${selector}`);
          }
          continue;
        }
        if (el instanceof HTMLElement) return { flow, el };
      }
    }
    return null;
  }
}

/**
 * Per-button prefill from `data-ehr-*` attributes, so one page can host many
 * contextual entry points without any JavaScript:
 *
 *   <button data-ehr-widget="find-doctor"
 *           data-ehr-provider-id="prv_8812"
 *           data-ehr-specialty="cardiology">Book with Dr. Rao</button>
 *
 * `data-ehr-provider-id` -> `providerId`. `data-ehr-widget` itself is the selector hook
 * and is skipped.
 */
export function readPrefill(el: HTMLElement): PrefillData {
  const out: PrefillData = {};
  for (const [key, value] of Object.entries(el.dataset)) {
    if (!key.startsWith('ehr') || key === 'ehrWidget' || value === undefined) continue;
    const name = key.slice(3);
    if (!name) continue;
    out[name.charAt(0).toLowerCase() + name.slice(1)] = value;
  }
  return out;
}
